/**
 * Glossary Links Utility
 *
 * This module provides utilities for creating and handling special glossary links
 * that the marketing team can use in their content to automatically open the
 * glossary to a specific term.
 */

export interface GlossaryLinkConfig {
  term: string;
  search?: string;
  letter?: string;
  autoFocus?: boolean;
}

/**
 * Special data attributes that marketing team can use in their HTML
 */
export const GLOSSARY_LINK_ATTRIBUTES = {
  TERM: 'data-glossary-term',
  SEARCH: 'data-glossary-search',
  LETTER: 'data-glossary-letter',
  AUTO_FOCUS: 'data-glossary-auto-focus',
} as const;

/**
 * CSS classes for styling glossary links
 */
export const GLOSSARY_LINK_CLASSES = {
  LINK: 'glossary-link',
  ACTIVE: 'glossary-link-active',
  LOADING: 'glossary-link-loading',
} as const;

/**
 * Creates a special glossary link element
 * Usage for marketing team:
 *
 * <a href="#"
 *    data-glossary-term="alignment-standards"
 *    class="glossary-link">
 *    Alignment to Standards
 * </a>
 *
 * Or with search:
 * <a href="#"
 *    data-glossary-search="fluency"
 *    class="glossary-link">
 *    Learn about Fluency
 * </a>
 */
export function createGlossaryLink(
  config: GlossaryLinkConfig,
  text: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = '#';
  link.className = GLOSSARY_LINK_CLASSES.LINK;
  link.textContent = text;

  // Set data attributes
  if (config.term) {
    link.setAttribute(GLOSSARY_LINK_ATTRIBUTES.TERM, config.term);
  }
  if (config.search) {
    link.setAttribute(GLOSSARY_LINK_ATTRIBUTES.SEARCH, config.search);
  }
  if (config.letter) {
    link.setAttribute(GLOSSARY_LINK_ATTRIBUTES.LETTER, config.letter);
  }
  if (config.autoFocus) {
    link.setAttribute(GLOSSARY_LINK_ATTRIBUTES.AUTO_FOCUS, 'true');
  }

  return link;
}

/**
 * Extracts glossary configuration from a link element
 */
export function extractGlossaryConfig(
  element: HTMLElement,
): GlossaryLinkConfig | null {
  const term = element.getAttribute(GLOSSARY_LINK_ATTRIBUTES.TERM);
  const search = element.getAttribute(GLOSSARY_LINK_ATTRIBUTES.SEARCH);
  const letter = element.getAttribute(GLOSSARY_LINK_ATTRIBUTES.LETTER);
  const autoFocus =
    element.getAttribute(GLOSSARY_LINK_ATTRIBUTES.AUTO_FOCUS) === 'true';

  // At least one of these should be present
  if (!term && !search && !letter) {
    return null;
  }

  return {
    term: term || '',
    search: search || undefined,
    letter: letter || undefined,
    autoFocus,
  };
}

/**
 * Checks if an element is a glossary link
 */
export function isGlossaryLink(element: HTMLElement): boolean {
  return (
    element.hasAttribute(GLOSSARY_LINK_ATTRIBUTES.TERM) ||
    element.hasAttribute(GLOSSARY_LINK_ATTRIBUTES.SEARCH) ||
    element.hasAttribute(GLOSSARY_LINK_ATTRIBUTES.LETTER) ||
    element.classList.contains(GLOSSARY_LINK_CLASSES.LINK)
  );
}

/**
 * URL-based glossary opening
 * Examples:
 * - /reports/some-report#glossary:alignment-standards
 * - /reports/some-report#glossary:search:fluency
 * - /reports/some-report#glossary:letter:a
 */
export function parseGlossaryHash(hash: string): GlossaryLinkConfig | null {
  if (!hash.startsWith('#glossary:')) {
    return null;
  }

  const parts = hash.substring(10).split(':'); // Remove '#glossary:'
  if (parts.length === 0) {
    return null;
  }

  const config: GlossaryLinkConfig = { term: '' };

  // Handle different formats
  if (parts.length === 1) {
    // Simple term: #glossary:term-slug
    config.term = parts[0];
  } else if (parts.length === 2) {
    // Action format: #glossary:search:query or #glossary:letter:a
    const [action, value] = parts;
    if (action === 'search') {
      config.search = value;
    } else if (action === 'letter') {
      config.letter = value;
    } else if (action === 'term') {
      config.term = value;
    }
  }

  return config;
}

/**
 * Creates a glossary hash URL for a given configuration
 */
export function createGlossaryHash(config: GlossaryLinkConfig): string {
  if (config.term) {
    return `#glossary:${config.term}`;
  } else if (config.search) {
    return `#glossary:search:${config.search}`;
  } else if (config.letter) {
    return `#glossary:letter:${config.letter}`;
  }
  return '#glossary:';
}

/**
 * Marketing team helper: Generates the HTML code they can copy-paste
 */
export function generateGlossaryLinkHTML(
  config: GlossaryLinkConfig,
  text: string,
): string {
  const attributes: string[] = [`class="${GLOSSARY_LINK_CLASSES.LINK}"`];

  if (config.term) {
    attributes.push(`${GLOSSARY_LINK_ATTRIBUTES.TERM}="${config.term}"`);
  }
  if (config.search) {
    attributes.push(`${GLOSSARY_LINK_ATTRIBUTES.SEARCH}="${config.search}"`);
  }
  if (config.letter) {
    attributes.push(`${GLOSSARY_LINK_ATTRIBUTES.LETTER}="${config.letter}"`);
  }
  if (config.autoFocus) {
    attributes.push(`${GLOSSARY_LINK_ATTRIBUTES.AUTO_FOCUS}="true"`);
  }

  return `<a href="#" ${attributes.join(' ')}>${text}</a>`;
}

/**
 * Documentation for marketing team
 */
export const MARKETING_TEAM_DOCUMENTATION = `
## Glossary Links for Marketing Team

You can create special links that automatically open the glossary to a specific term using the following methods:

### Method 1: Data Attributes (Recommended)
Use these HTML attributes in your content:

**Open glossary to specific term:**
<a href="#" data-glossary-term="alignment-standards" class="glossary-link">
  Alignment to Standards
</a>

**Open glossary with search:**
<a href="#" data-glossary-search="fluency" class="glossary-link">
  Learn about Fluency
</a>

**Open glossary to specific letter:**
<a href="#" data-glossary-letter="a" class="glossary-link">
  Browse A terms
</a>

### Method 2: URL Hash (For direct links)
Add these to page URLs:

- Open to specific term: #glossary:alignment-standards
- Open with search: #glossary:search:fluency  
- Open to letter: #glossary:letter:a

### Styling
All glossary links automatically get the 'glossary-link' class for styling.

### Required Attributes
- data-glossary-term: Opens glossary to specific term slug
- data-glossary-search: Opens glossary with search query
- data-glossary-letter: Opens glossary filtered to specific letter
- data-glossary-auto-focus: Auto-focuses search field (optional)

### Examples in CMS
When writing articles, you can use:

<a href="#" data-glossary-term="science-of-reading" class="glossary-link">
  Science of Reading
</a>

<a href="#" data-glossary-search="alignment" class="glossary-link">
  Learn more about alignment
</a>
`;
